import { Button, Clickable, Col, Hidden, Row } from '@ahmdigital/ui';
import { endsWith } from 'lodash/fp';
import { EXTRAS_TYPE } from '@ahmdigital/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import React from 'react';

import {
  GROWTHBOOK_PRODUCT_DETAILS_CHANGE_THIS,
  MEDIA_SIZE,
  PREMIUM_REVIEW_TOGGLE_STYLE,
} from '../../ahm-constants/ui';
import { HeaderWrapper, LargeDevicePremiumReviewToggleWrapper, StyledStrong } from './styles';
import { MediaDisplay } from '../../styles/styles';
import { ProductType } from '../../types/product';
import constants from '../../ahm-constants';
import DesktopHeaderFilterBar from './components/filter-bar-header';
import getTitleFromType from '../../utils/get-title-from-type';
import NavigationHeaderTitle, { NavigationUpliftHeaderTitle } from './components/navigation-header-title';
import PremiumReviewToggle from '../premium-review-toggle-wrapper';
import styles from './styles.module.sass';
import UpliftedHeader from './components/filter-bar-header/uplifted-header';

type NavigationHeaderProps = {
  isCombiningHospitalWithExtras?: boolean;
  isProductListingPage?: boolean;
  isProductListingPageAll?: boolean;
  needsProductType?: ProductType['extrasType'];
  needsType?: string;
  productExtrasType?: ProductType['extrasType'];
  productQuantity?: number;
  productShortName?: string;
  productType: string;
  products?: ProductType[];
  showPremiumReviewToggle?: boolean;
  totalProducts?: number;
  useHeading?: boolean;
};

const NavigationHeader = ({
  isCombiningHospitalWithExtras,
  needsProductType,
  needsType,
  productExtrasType,
  productQuantity,
  totalProducts,
  products,
  productShortName,
  productType,
  showPremiumReviewToggle,
  useHeading,
  isProductListingPage,
  isProductListingPageAll,
}: NavigationHeaderProps) => {
  const router = useRouter();
  const isProductDetailsChangeThisTestOn = useFeatureIsOn(GROWTHBOOK_PRODUCT_DETAILS_CHANGE_THIS);
  const isChoosableExtra = productExtrasType === EXTRAS_TYPE.CHOOSABLE;
  let changeUrl = constants.URLS.SALES.FIND_HEALTH_INSURANCE;

  const isProductListingPageAdd = endsWith('/add', router.asPath);
  const isProductPage = !(isProductListingPage || isProductListingPageAdd);

  if (isChoosableExtra) {
    changeUrl = constants.URLS.SALES.CHOOSABLE_PICKER;
  }

  const title = isChoosableExtra ? productShortName : getTitleFromType({ needsProductType, needsType, productType });

  const upliftTitle = `Showing ${totalProducts} ${title.substring(0, title.lastIndexOf(' '))} covers`;

  const productQuantityText =
    // @ts-expect-error - Automatic, Please fix when editing this file
    totalProducts && productQuantity < totalProducts ? `${productQuantity} of ${totalProducts}` : productQuantity;

  let titlePrefix = productQuantity ? `${productQuantityText} options for` : 'I’m looking for';
  if (isCombiningHospitalWithExtras) {
    titlePrefix = 'Add';
  }

  const titleSuffix = totalProducts && productQuantity && productQuantity < totalProducts ? 'based on the below' : '';

  const hideChangeThis = isProductPage && isProductDetailsChangeThisTestOn;

  const renderPremiumReviewToggle = (isProductListing: boolean) => {
    const ToggleComponent = (
      <PremiumReviewToggle
        type={PREMIUM_REVIEW_TOGGLE_STYLE.FLAT_WHITE}
        isProductPage={!isProductListing}
        showBackground={false}
      />
    );
    if (isProductListing)
      return (
        <MediaDisplay up media={MEDIA_SIZE.MD}>
          <LargeDevicePremiumReviewToggleWrapper>{ToggleComponent}</LargeDevicePremiumReviewToggleWrapper>
        </MediaDisplay>
      );

    return <MediaDisplay media={MEDIA_SIZE.MD}>{ToggleComponent}</MediaDisplay>;
  };

  const renderPremiumReviewToggleUplift = () => {
    const ToggleComponent = (
      <>
        <Hidden xs sm>
          <PremiumReviewToggle showBackground={false} type={PREMIUM_REVIEW_TOGGLE_STYLE.INLINE} />
        </Hidden>
        <Hidden md df lg>
          <PremiumReviewToggle showBackground={false} type={PREMIUM_REVIEW_TOGGLE_STYLE.COMPARER_PAGE} />
        </Hidden>
      </>
    );

    return ToggleComponent;
  };

  const filterBarProps = {
    changeUrl,
    isCombiningHospitalWithExtras,
    isProductListingPage,
    isProductListingPageAdd,
    isProductListingPageAll,
    needsProductType,
    needsType,
    products,
    showPremiumReviewToggle,
    title,
    titlePrefix,
    titleSuffix,
    useHeading,
  };

  if (hideChangeThis) {
    return null;
  }

  return (
    <>
      <HeaderWrapper isProductPage={isProductPage}>
        <MediaDisplay media={MEDIA_SIZE.MD} up>
          <DesktopHeaderFilterBar {...filterBarProps} />
        </MediaDisplay>
        <MediaDisplay media={MEDIA_SIZE.MD}>
          {isProductListingPageAll ? (
            <UpliftedHeader needsType={needsType} products={products} />
          ) : (
            // @ts-expect-error - Automatic, Please fix when editing this file
            <div className={classNames(styles.wrapper, { [styles.productPage]: isProductPage })}>
              <div className={styles.gridWrapper}>
                {isCombiningHospitalWithExtras && (
                  <Clickable
                    isUnstyled
                    icon={{ name: 'chevron-left' }}
                    onClick={router.back}
                    variant={styles.backButton}
                  >
                    <Hidden xs sm>
                      Back
                    </Hidden>
                  </Clickable>
                )}
                {/* @ts-expect-error - Automatic, Please fix when editing this file */}
                <div className={classNames(styles.gridColumns, { [styles.productPage]: isProductPage })}>
                  <Row lgHorizontalAlign="center" verticalAlign="middle">
                    <Col sm={12}>
                      <NavigationHeaderTitle useHeading={useHeading}>
                        <span data-testid="md-navigation-header-title">
                          {titlePrefix} <StyledStrong>{title}</StyledStrong> {titleSuffix}
                        </span>
                      </NavigationHeaderTitle>
                    </Col>
                    {!isCombiningHospitalWithExtras && (
                      <Col sm={12}>
                        <Button
                          to={changeUrl}
                          // @ts-expect-error - Automatic, Please fix when editing this file
                          className={classNames(styles.button, { [styles.productPage]: isProductPage })}
                          variant="white"
                        >
                          Change this
                        </Button>
                      </Col>
                    )}
                  </Row>
                </div>
              </div>
            </div>
          )}
        </MediaDisplay>
        {!isProductListingPageAll && showPremiumReviewToggle && renderPremiumReviewToggle(!isProductPage)}
      </HeaderWrapper>
      {isProductListingPageAll && (
        <NavigationUpliftHeaderTitle useHeading={useHeading}>
          <span data-testid="md-navigation-header-title">{upliftTitle}</span>
          {showPremiumReviewToggle && renderPremiumReviewToggleUplift()}
        </NavigationUpliftHeaderTitle>
      )}
    </>
  );
};

NavigationHeader.defaultProps = {
  isCombiningHospitalWithExtras: false,
  needsProductType: null,
  needsType: null,
  productExtrasType: null,
  productQuantity: null,
  productShortName: null,
  products: null,
  showPremiumReviewToggle: true,
  totalProducts: null,
  useHeading: false,
};

export { NavigationHeader as NavigationHeaderForTest };

export default NavigationHeader;
